import { useMutation, useQuery } from "@tanstack/react-query";
import { api } from "api";
import { T_Api_Success_Res } from "./api.types";
import { DISBURSEMENT_STATUS } from "dtos/ledger.dto";
import { TransactionDto } from "dtos/wallet.transaction.dto";

export const ledgerService = {
  useModifyRows: () => {
    return useMutation({
      mutationKey: ["ledger", "modify-rows"],
      mutationFn: async (body: {
        application_id: string;
        count: number;
        program_fee?: number;
        creditor_amount?: number;
      }) => {
        const res = await api.post(`/ledger/modify-rows`, body);
        return res.data as T_Api_Success_Res<any>;
      },
    });
  },
  useMarkPaymentManually: () => {
    return useMutation({
      mutationKey: ["ledger", "mark-payment"],
      mutationFn: async ({
        body,
      }: {
        body: {
          program_fee?: number;
          service_fee?: number;
          legal_fee?: number;
          creditor_total?: number;
          idx: number;
          application_id: string;
          creditors?: any;
          settlementType?: "automation" | "manual";
        };
      }) => {
        const res = await api.post(`/ledger/process-payment/manual`, body);

        return res.data as T_Api_Success_Res<object>;
      },
    });
  },

  useMarkFullyPaid: () => {
    return useMutation({
      mutationKey: ["ledger", "mark-payment-fully"],
      mutationFn: async ({
        body,
      }: {
        body: {
          idx: number;
          application_id: string;
          creditors?: any;
          ref_transaction_id?: string;
          posting_date?: Date;
          settlementType?: "automation" | "manual";
        };
      }) => {
        const res = await api.post(`/ledger/process-full-payment/manual`, body);

        return res.data as T_Api_Success_Res<object>;
      },
    });
  },

  useMarkPaymentAffiliate: () => {
    return useMutation({
      mutationKey: ["ledger", "mark-payment-affiliate"],
      mutationFn: async ({
        body,
      }: {
        body: {
          id: string;
          affiliate_id: string;
          idx: number;
          disbursement_date?: string;
        };
      }) => {
        const res = await api.post(`/ledger/mark-payment/affiliate`, body);
      },
    });
  },

  useModifyStatusAffiliate: () => {
    return useMutation({
      mutationKey: ["ledger", "modify-status-affiliate"],
      mutationFn: async ({
        body,
      }: {
        body: {
          id: string;
          affiliate_id: string;
          idx: number;
          disbursement_status: DISBURSEMENT_STATUS;
        };
      }) => {
        const res = await api.post(`/ledger/change-status/affiliate`, body);
      },
    });
  },

  useGetTransactionLogs: (id: string | undefined) => {
    return useQuery({
      queryKey: ["ledger/transaction-logs", id],
      queryFn: async () => {
        const res = await api.get(`/ledger/row-logs/${id}`);
        return res.data as T_Api_Success_Res<TransactionDto[]>;
      },
      enabled: !!id,
    });
  },

  useGetBalance: (id: string | undefined) => {
    return useQuery({
      queryKey: ["ledger/budget", id],
      queryFn: async () => {
        const res = await api.get(`/ledger/wallet/budget/${id}`);
        return res.data as T_Api_Success_Res<{ balance: number }>;
      },
      enabled: !!id,
    });
  },
  useCreatePercentageLedger: () => {
    return useMutation({
      mutationKey: ["create-percentage-ledger"],
      mutationFn: async ({ appKey, lastWeek, weeks }: { appKey?: string; lastWeek?: number; weeks?: number }) => {
        const res = await api.post("/ledger/percentage", { appKey, lastWeek, weeks });
        return res.data;
      },
    });
  },

  useStartPaymentAutomation: () => {
    return useMutation({
      mutationKey: ["ledger/start-payment-automation"],
      mutationFn: async ({ appkey, idx }: { appkey: string; idx: number }) => {
        const res = await api.post("/ledger/automation/payments", { appkey, idx });
        return res.data;
      },
    });
  },

  usePauseLedgerRow: () => {
    return useMutation({
      mutationKey: ["ledger/row-pause"],
      mutationFn: async ({ ledger_id }: { ledger_id: string }) => {
        const res = await api.post("/ledger/row/pause", { ledger_id });
        return res.data as T_Api_Success_Res<object>;
      },
    });
  },

  useModifyPostingDate: () => {
    return useMutation({
      mutationKey: ["ledger/modify-posting-date"],
      mutationFn: async ({
        ledger_id,
        cascade = false,
        newDayOfWeek,
      }: {
        ledger_id: string;
        cascade: boolean;
        newDayOfWeek: number;
      }) => {
        const res = await api.post("ledger/row/modify-posting-date", { ledger_id, cascade, newDayOfWeek });
        return res.data as T_Api_Success_Res<object>;
      },
    });
  },
};
